/**
 * Create workspace, notifications and email sends (migration 20261029130000).
 */
import type { Transport } from './transport.ts';

// ---------- Workspaces ----------
export interface Plan { id: string; code: string; name: string }

export async function listPlans(transport: Transport, signal?: AbortSignal): Promise<Plan[]> {
  const rows = await transport.rpc<{ plan_id: string; code: string; display_name: string }[]>('list_plans', {}, signal);
  return rows.map(r => ({ id: r.plan_id, code: r.code, name: r.display_name }));
}

/** Creates a workspace owned by the caller. Reusing the request ID returns the same workspace. */
export function createWorkspace(transport: Transport, input: { requestId: string; name: string; timeZone: string }, signal?: AbortSignal): Promise<string> {
  return transport.rpc<string>('create_workspace', { request: input.requestId, workspace_name: input.name.trim(), zone: input.timeZone }, signal);
}

export const WORKSPACE_NAME_MAX = 200;

// ---------- Notifications ----------
export type NotificationKind = 'lfd_today' | 'pickup_due' | 'evidence_missing' | 'invitation_received' | 'send_accepted' | 'send_failed' | 'send_unknown'
  | 'handover_requested' | 'handover_answered' | 'change_requested' | 'fetch_failed' | 'security_change' | 'new_sign_in' | 'storage_running' | 'send_bounced' | 'send_replied';
export interface Notice {
  /** Created during quiet hours and still inside them: listed, but not alerting. */
  deferred: boolean;
  id: string;
  kind: NotificationKind;
  targetKind: 'order' | 'send_operation' | 'invitation' | 'handover' | 'change_request' | 'fetch_run' | null;
  targetId: string | null;
  quantity: number | null;
  label: string | null;
  createdAt: number;
  read: boolean;
}

export async function listNotifications(transport: Transport, workspaceId: string, signal?: AbortSignal): Promise<Notice[]> {
  const rows = await transport.rpc<{ id: string; kind: NotificationKind; target_kind: Notice['targetKind']; target_id: string | null; quantity: number | null; label: string | null; created_at: string; read_at: string | null; deferred: boolean }[]>(
    'list_my_notifications', { w: workspaceId }, signal);
  return rows.map(r => ({ id: r.id, kind: r.kind, targetKind: r.target_kind, targetId: r.target_id, quantity: r.quantity, label: r.label, createdAt: Date.parse(r.created_at), read: r.read_at !== null, deferred: r.deferred === true }));
}

/** Marks the given notices read, or all of them when `ids` is omitted. */
export function markNotificationsRead(transport: Transport, workspaceId: string, ids?: string[]): Promise<number> {
  return transport.rpc<number>('mark_notifications_read', { w: workspaceId, ids: ids ?? null });
}

/** "2 min ago" style age for notification rows. */
export function ageLabel(ms: number, now = Date.now()): { unit: 'now' | 'minute' | 'hour' | 'day'; value: number } {
  const minutes = Math.floor((now - ms) / 60000);
  if (minutes < 1) return { unit: 'now', value: 0 };
  if (minutes < 60) return { unit: 'minute', value: minutes };
  if (minutes < 1440) return { unit: 'hour', value: Math.floor(minutes / 60) };
  return { unit: 'day', value: Math.floor(minutes / 1440) };
}

// ---------- Email sends ----------
export interface SendConnection { id: string; label: string; status: string }

export async function listSendConnections(transport: Transport, workspaceId: string, signal?: AbortSignal): Promise<SendConnection[]> {
  const rows = await transport.rpc<{ connection_id: string; label: string; status: string }[]>('list_send_connections', { w: workspaceId }, signal);
  return rows.map(r => ({ id: r.connection_id, label: r.label, status: r.status }));
}

export const EMAIL_PATTERN = /^[^@\s<>,;"]+@[^@\s<>,;"]+\.[^@\s<>,;"]+$/;
export const SEND_LIMITS = { recipients: 20, subject: 200, body: 10000, attachments: 100, bytes: 20971520 } as const;

/** Splits "a@x.com, b@y.com; c@z.com" into addresses; returns the invalid ones separately. */
export function parseRecipients(text: string): { valid: string[]; invalid: string[] } {
  const parts = text.split(/[,;\s]+/).map(p => p.trim()).filter(Boolean);
  const valid = [...new Set(parts.filter(p => EMAIL_PATTERN.test(p) && p.length <= 254).map(p => p.toLowerCase()))];
  return { valid, invalid: parts.filter(p => !EMAIL_PATTERN.test(p) || p.length > 254) };
}

export interface EmailSend {
  workspaceId: string; requestId: string; orderId: string; connectionId: string;
  recipients: string[]; subject: string; body: string;
  /** Exact document versions the sender reviewed (never "latest at send time"). */
  versionIds: string[];
}

export function createEmailSend(transport: Transport, s: EmailSend, signal?: AbortSignal): Promise<string> {
  return transport.rpc<string>('create_email_send', {
    w: s.workspaceId, request: s.requestId, target_order: s.orderId, connection: s.connectionId,
    recipients: s.recipients, subject_text: s.subject, body: s.body, version_ids: s.versionIds,
  }, signal);
}

/** Canonical approved payload, matching the server's digest inputs. */
export function sendPayloadKey(s: Omit<EmailSend, 'workspaceId' | 'requestId'>): string {
  return JSON.stringify([s.orderId, s.connectionId, [...new Set(s.recipients.map(r => r.trim().toLowerCase()))].sort(), s.subject.trim(), s.body, [...new Set(s.versionIds)].sort()]);
}

/**
 * Durable record of a send attempt, kept on the device until its outcome is known.
 * It makes a lost answer (reload, timeout) resume the same request instead of sending twice.
 */
export interface PendingSend { requestId: string; payload: string; operationId: string | null }

export type SendPlan = { kind: 'go'; requestId: string; resumed: boolean } | { kind: 'unresolved'; pending: PendingSend };

/** Same payload → same request ID; a different payload is allowed only once the previous outcome is settled. */
export function planSend(pending: PendingSend | null, payload: string, settled: boolean, newId: () => string = () => crypto.randomUUID()): SendPlan {
  if (pending && pending.payload === payload) return { kind: 'go', requestId: pending.requestId, resumed: true };
  if (pending && !settled) return { kind: 'unresolved', pending };
  return { kind: 'go', requestId: newId(), resumed: false };
}

export async function readMySendRequest(transport: Transport, workspaceId: string, requestId: string): Promise<{ operationId: string; state: SendState } | null> {
  const [row] = await transport.rpc<{ operation_id: string; state: SendState }[]>('read_my_send_request', { w: workspaceId, request: requestId });
  return row ? { operationId: row.operation_id, state: row.state } : null;
}

/** Outcome states after which nothing more can happen to a send. */
export const SETTLED_SEND: readonly SendState[] = ['accepted', 'failed', 'cancelled'];

export type SendState = 'draft' | 'approved' | 'sending' | 'accepted' | 'failed' | 'unknown' | 'cancelled';

export async function readMySend(transport: Transport, workspaceId: string, operationId: string): Promise<{ state: SendState; recipients: string[]; attachments: number } | null> {
  const [row] = await transport.rpc<{ state: SendState; recipients: string[] | null; attachment_count: number }[]>('read_my_send', { w: workspaceId, operation: operationId });
  return row ? { state: row.state, recipients: row.recipients ?? [], attachments: row.attachment_count } : null;
}
